import axios from "axios";
import { API } from "../constants";
import { handleResponse } from "../utils/handleResponse";
import { phdScannerApiAxios } from "../lib/axios-interceptor";

type PARAMTYPE = Record<string, string | number | boolean>;

export const ApplicantService = {
  // Create a separate axios instance for SaaS calls
  createSaasAxios() {
    const saasAxios = axios.create({
      baseURL: API,
      timeout: 120000,
      withCredentials: true,
    });
    
    // Add interceptor for SaaS authentication
    saasAxios.interceptors.request.use((config) => {
      const headers = config.headers || {};
      
      // Get token from localStorage for SaaS users
      const accessToken = localStorage.getItem("token");
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      } else {
        console.warn('No token found in localStorage');
      }
      
      // Add tenant header
      const tenant = localStorage.getItem("tenant");
      if (tenant) {
        headers["X-Tenant"] = tenant;
        console.log('Using tenant:', tenant);
      } else {
        console.warn('No tenant found in localStorage');
      }
      
      console.log('Request headers:', headers);
      return config;
    });
    
    return saasAxios;
  },

  // Handle SaaS auth errors
  handleSaasAuthError(error: any) {
    const status = error?.response?.status;
    const message = error?.response?.data?.error || error?.message;
    if (
      status === 401 || status === 403 ||
      (typeof message === 'string' &&
        (message.toLowerCase().includes('token expired') ||
         message.toLowerCase().includes('invalid token') ||
         message.toLowerCase().includes('not authenticated')))
    ) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('tenant');
      
      // Redirect to correct login path based on domain
      const isSaaSSubdomain = window.location.hostname !== 'phdscanner.com' && 
                             window.location.hostname !== 'www.phdscanner.com' &&
                             !window.location.hostname.includes('localhost');
      
      if (isSaaSSubdomain) {
        window.location.href = '/login';
      } else {
        window.location.href = '/saas/login';
      }
      return true;
    }
    return false;
  },

  // Upload resume and create applicant
  async uploadResume(formData: FormData) {
    // Use phdScannerApiAxios for regular user upload with authentication
    return handleResponse(async () => {
      try {
        return await phdScannerApiAxios.post(`/api/applicants/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } catch (error) {
        throw error;
      }
    });
  },

  // Get applicants for an opportunity
  async getApplicants(params: PARAMTYPE) {
    const saasAxios = this.createSaasAxios();
    
    return handleResponse(async () => {
      try {
        const response = await saasAxios.get("/api/applicants", { params });
        return response;
      } catch (error) {
        console.error('Error in getApplicants:', error);
        if (this.handleSaasAuthError(error)) return {};
        throw error;
      }
    });
  },

  // Get a specific applicant by ID
  async getApplicantById(id: string) {
    const saasAxios = this.createSaasAxios();
    
    return handleResponse(async () => {
      try {
        return await saasAxios.get(`/api/applicants/${id}`);
      } catch (error) {
        if (this.handleSaasAuthError(error)) return {};
        throw error;
      }
    });
  },

  // Update applicant status
  async updateApplicantStatus(id: string, data: { status: string }) {
    const saasAxios = this.createSaasAxios();
    
    return handleResponse(async () => {
      try {
        return await saasAxios.put(`/api/applicants/${id}/status`, data);
      } catch (error) {
        if (this.handleSaasAuthError(error)) return {};
        throw error;
      }
    });
  },

  // Create a comment for an applicant
  async createComment(applicantId: string, comment: string) {
    const saasAxios = this.createSaasAxios();
    
    return handleResponse(async () => {
      try {
        return await saasAxios.post(`/api/applicants/${applicantId}/comments`, { comment });
      } catch (error) {
        if (this.handleSaasAuthError(error)) return {};
        throw error;
      }
    });
  },

  // Get comments for an applicant
  async getComments(applicantId: string) {
    const saasAxios = this.createSaasAxios();
    
    return handleResponse(async () => {
      try {
        return await saasAxios.get(`/api/applicants/${applicantId}/comments`);
      } catch (error) {
        if (this.handleSaasAuthError(error)) return {};
        throw error;
      }
    });
  },

  // Delete a comment
  async deleteComment(applicantId: string, commentId: string) {
    const saasAxios = this.createSaasAxios();
    
    return handleResponse(async () => {
      try {
        return await saasAxios.delete(`/api/applicants/${applicantId}/comments/${commentId}`);
      } catch (error) {
        if (this.handleSaasAuthError(error)) return {};
        throw error;
      }
    });
  },

  // Delete an applicant
  async deleteApplicant(id: string) {
    const saasAxios = this.createSaasAxios();
    
    return handleResponse(async () => {
      try {
        return await saasAxios.delete(`/api/applicants/${id}`);
      } catch (error) {
        if (this.handleSaasAuthError(error)) return {};
        throw error;
      }
    });
  },

  // Download resume
  async downloadResume(id: string) {
    const saasAxios = this.createSaasAxios();
    
    return handleResponse(async () => {
      try {
        return await saasAxios.get(`/api/applicants/${id}/download`);
      } catch (error) {
        if (this.handleSaasAuthError(error)) return {};
        throw error;
      }
    });
  },

  // Get applicant statistics
  async getApplicantStats(params: PARAMTYPE = {}) {
    const saasAxios = this.createSaasAxios();
    
    return handleResponse(async () => {
      try {
        return await saasAxios.get("/api/applicants/stats", { params });
      } catch (error) {
        if (this.handleSaasAuthError(error)) return {};
        throw error;
      }
    });
  },
}; 